/* Legends cart-drawer upsell — pure selection logic, no DOM and no network.
   Loaded before legends-cart-drawer.js, which draws the cards and performs the adds.

   Offers every band-matched accessory for whatever bands are in the cart (the £15 hard
   case and, since 2026-09-04, the £10 cleaning cloth 2-pack), plus the extras (the £10
   Lens Cleaner Kit) whatever the band. Since 2026-11-03 it also offers accessories tied to a
   product TYPE (the £20 low-light lens, offered only when a snow goggle, type "Goggles", is in
   the cart), and offers a goggle its own band's cleaning cloth pack and the Lens Cleaner Kit
   (tag also-with:Goggles). Replaced the "recommendations"
   carousel built in June 2026 and never switched on, which also pulled in other frames
   from the same band — James, 2026-08-28: a customer in the cart has already looked at
   the frames, so the row is accessories only.

   Stock is not decided here. Liquid drops any variant that is not available before the
   config reaches the browser, so a sold-out case simply has no entry and this file has
   nothing to filter. That keeps one rule in one place: whatever Shopify will sell is
   whatever gets offered. */
(function (window) {
  'use strict';

  /* The product a cart line belongs to. Matched on this and never on the handle:
     Shopify translates handles per locale, so a map rendered on /de/ never matches
     /cart.js, which always answers in the default locale. That is what silently cost
     orders #LOE-1141 and #LOE-1144 their free case on 2026-08-18. Ids do not translate.
     /cart.js always carries product_id; a hand-built line may not. */
  function lineProductId(item) {
    if (!item || item.product_id == null) return null;
    return String(item.product_id);
  }

  /* The selling plan a cart line was bought on, or null. /cart.js nests it under
     selling_plan_allocation; a hand-built line may not carry it at all. */
  function linePlanId(item) {
    var alloc = item && item.selling_plan_allocation;
    if (!alloc || !alloc.selling_plan || alloc.selling_plan.id == null) return null;
    return alloc.selling_plan.id;
  }

  /* Whether an item can join the frames' delivery group.

     Shopify groups line items into shipments by delivery expectation, so an item added
     without the frames' pre-order plan sits in its own "ships now" group against their
     dated one and the customer is billed postage twice — UK £7.98 against £3.99,
     measured at real checkout on 2026-08-11. An item that cannot take the plan is
     therefore not offered at all rather than offered at that cost.

     planIds is what the variant's own selling_plan_allocations say, so this answers
     itself as the catalogue changes: put the item on the STOQ offer and it appears,
     take pre-order off the frames and everything qualifies. */
  function canJoin(item, planId) {
    // A pre-order item in a cart with no plan needs a plan of its own; see card().
    if (planId == null) return !(item && item.preorder) || ownPlan(item) != null;
    var plans = (item && item.planIds) || [];
    for (var i = 0; i < plans.length; i++) {
      if (String(plans[i]) === String(planId)) return true;
    }
    return false;
  }

  /* A pre-order item's own plan, for a cart that has none: any plan it accepts will do,
     because legends-ship-together.js re-reads the cart after the add, puts the item on its
     own date and carries the rest of the basket with it. Adding it with NO plan would sell
     a pre-order as stock, in a ships-now group with its own postage. */
  function ownPlan(item) {
    var plans = (item && item.planIds) || [];
    return plans.length ? plans[0] : null;
  }

  /* Whether a band accessory is also offered against a product type, by its
     `also-with:<type>` tags (legends-upsell-item.liquid emits them as alsoWith). */
  function alsoWith(item, type) {
    var list = (item && item.alsoWith) || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i]) === String(type)) return true;
    }
    return false;
  }

  function card(item, planId) {
    if (planId == null && item.preorder) planId = ownPlan(item);
    return {
      productId: item.productId,
      variantId: item.variantId,
      title: item.title,
      price: item.price,
      image: item.image,
      url: item.url,
      sellingPlanId: planId
    };
  }

  /* Everything offered against one band, dearest first.

     This was a single item per band ("caseByBand") until 2026-09-04, when the cleaning
     cloth 2-packs became a SECOND band-tagged accessory. A one-per-band map could not
     hold them: Liquid emitted the same band tag as a duplicate JSON key and JSON.parse
     keeps only the last, so the £15 case and the £10 cloth pack would have quietly
     replaced each other depending on how the collection happened to sort that day.

     Ordered here rather than in Liquid because the cart-accessories collection sorts by
     BEST_SELLING, which reshuffles itself as the store trades. Price descending keeps the
     case leading the cloth pack whatever the collection does; equal prices fall back to
     title so the order is at least stable between renders. */
  function rankedForBand(byBand, band) {
    var list = byBand[band];
    if (list == null) return [];
    // Tolerates the pre-2026-09-04 shape, for a page opened before the theme push whose
    // cached config still holds one object per band rather than an array.
    if (Object.prototype.toString.call(list) !== '[object Array]') list = [list];
    return list.slice().sort(function (a, b) {
      var pa = (a && a.price) || 0;
      var pb = (b && b.price) || 0;
      if (pa !== pb) return pb - pa;
      return String((a && a.title) || '').localeCompare(String((b && b.title) || ''));
    });
  }

  /* The accessories to offer for the cart as it stands, in the order they should read:
     the band-matched accessories first, in the order their bands first appear in the
     cart, then the extras. Pure: performs no I/O and mutates nothing.

     Returns [] when the cart holds no frame we recognise and no product whose type has
     accessories. The row completes a purchase, so a cart holding only accessories gets nothing. */
  function computeUpsells(cart, config) {
    if (!cart || !cart.items || !config || !config.enabled) return [];

    var frameBands = config.frameBandByProductId || {};
    // caseByBand is the pre-2026-09-04 key, kept only for a page whose config was rendered
    // before the theme push. Nothing emits it any more.
    var byBand = config.accessoriesByBand || config.caseByBand || {};
    var extras = config.extras || [];

    // Accessories tied to a product type rather than a band: the low-light lens fits the snow
    // goggles of every band, and nothing else. Matched on the cart line's product_type, which
    // /cart.js carries and which, unlike a handle, is not translated per locale.
    var byType = config.byProductType || {};
    // The band of each typed product (the goggles), so a band accessory tagged
    // `also-with:<type>` (the cleaning cloth 2-packs, James 2026-11-03) is offered against a
    // goggle of its own band, while one without the tag (the sunglasses cases) is not.
    var typedBands = config.typedBandByProductId || {};
    var typeOrder = [];   // types present that have accessories, first appearance first
    var typePlan = {};    // the plan the first line of each type that carries one was bought on
    var typeBands = {};   // type -> its bands present, first appearance first
    var t, tb;

    var bandOrder = [];   // bands present, first appearance first
    var seenBand = {};
    var inCart = {};      // product ids already in the cart, so nothing is offered twice
    var planId = null;    // the plan the frames were bought on
    var i, it, pid, band;

    for (i = 0; i < cart.items.length; i++) {
      it = cart.items[i];
      pid = lineProductId(it);
      if (pid == null) continue;
      inCart[pid] = true;

      t = it.product_type;
      tb = typedBands[pid];
      if (t && (Object.prototype.hasOwnProperty.call(byType, t) || tb)) {
        if (!Object.prototype.hasOwnProperty.call(typePlan, t)) {
          typeOrder.push(t);
          typePlan[t] = null;
          typeBands[t] = [];
        }
        if (typePlan[t] == null) typePlan[t] = linePlanId(it);
        if (tb && typeBands[t].indexOf(tb) === -1) typeBands[t].push(tb);
      }

      band = frameBands[pid];
      if (!band) continue;

      if (!seenBand[band]) {
        seenBand[band] = true;
        bandOrder.push(band);
      }
      // First frame that carries a plan wins; a plan-less frame must not veto a later
      // one that has one, which is how a cart part-built before a catalogue change reads.
      if (planId == null) planId = linePlanId(it);
    }

    if (!bandOrder.length && !typeOrder.length) return [];

    var offers = [];
    var j, c, ranked;
    for (i = 0; i < bandOrder.length; i++) {
      ranked = rankedForBand(byBand, bandOrder[i]);
      for (j = 0; j < ranked.length; j++) {
        c = ranked[j];
        if (c && !inCart[String(c.productId)] && canJoin(c, planId)) {
          offers.push(card(c, planId));
          inCart[String(c.productId)] = true;
        }
      }
    }
    // Each type's accessories travel on the plan of THAT type's line (a pre-ordered Iron Maiden
    // goggle), not on whatever plan the frames carry, and are offered once whatever else is in
    // the cart. inCart is updated so a type listed twice cannot offer the same item twice.
    for (i = 0; i < typeOrder.length; i++) {
      ranked = rankedForBand(byType, typeOrder[i]);
      for (j = 0; j < ranked.length; j++) {
        c = ranked[j];
        if (c && !inCart[String(c.productId)] && canJoin(c, typePlan[typeOrder[i]])) {
          offers.push(card(c, typePlan[typeOrder[i]]));
          inCart[String(c.productId)] = true;
        }
      }
      // Then the band accessories marked as fitting this type, for each of its bands present.
      // A frame of the same band already offered them above; inCart stops a second card.
      for (var k = 0; k < typeBands[typeOrder[i]].length; k++) {
        ranked = rankedForBand(byBand, typeBands[typeOrder[i]][k]);
        for (j = 0; j < ranked.length; j++) {
          c = ranked[j];
          if (c && alsoWith(c, typeOrder[i]) && !inCart[String(c.productId)] &&
              canJoin(c, typePlan[typeOrder[i]])) {
            offers.push(card(c, typePlan[typeOrder[i]]));
            inCart[String(c.productId)] = true;
          }
        }
      }
      // And the band-less extras marked as fitting this type (the Lens Cleaner Kit, tagged
      // also-with:Goggles, James 2026-11-03), whatever the goggle's band. Unlike the rest,
      // an in-stock extra that cannot take the goggle's plan is still offered, on NO plan:
      // the kit is kept off the Iron Maiden goggle offer on purpose (being on it would make
      // 20 Nov its native date and drag a frame pre-order cart holding it onto 20 Nov), and
      // legends-ship-together.js joins a plan-less line to the goggle's date, carried on the
      // November plan both share, so it is still one parcel and one postage charge.
      for (j = 0; j < extras.length; j++) {
        c = extras[j];
        if (c && alsoWith(c, typeOrder[i]) && !inCart[String(c.productId)]) {
          if (canJoin(c, typePlan[typeOrder[i]])) offers.push(card(c, typePlan[typeOrder[i]]));
          else if (!c.preorder) offers.push(card(c, null));
          else continue;
          inCart[String(c.productId)] = true;
        }
      }
    }
    // The extras complete a FRAME purchase, so a cart holding only goggles gets none.
    if (!bandOrder.length) return offers;
    for (i = 0; i < extras.length; i++) {
      var e = extras[i];
      if (e && !inCart[String(e.productId)] && canJoin(e, planId)) offers.push(card(e, planId));
    }
    return offers;
  }

  window.LegendsCartUpsell = { computeUpsells: computeUpsells };
})(window);
